import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AddUser from './AddUser';
import { getAllUsers, updateUserProfile, deleteUser, getUserScreenPermissions, getUserFeaturePermissions } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';

const UsersOnSystem = () => {
  const { userProfile } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers();
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const [screens, features] = await Promise.all([
        getUserScreenPermissions(userId),
        getUserFeaturePermissions(userId)
      ]);
      setUserPermissions(prev => ({
        ...prev,
        [userId]: { screens, features }
      }));
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleUserAdded = (newUser) => {
    setUsers(prev => [newUser, ...prev]);
    setShowAddUser(false);
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await updateUserProfile(userId, { status: newStatus });
      setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, status: newStatus } : u));
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUser(userId);
      setUsers(prev => prev?.filter(u => u?.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    await loadUserPermissions(user?.id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'inactive':
        return 'text-muted-foreground bg-muted';
      case 'suspended':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'CheckCircle2';
      case 'inactive':
        return 'XCircle';
      case 'suspended':
        return 'Ban';
      default:
        return 'Circle';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-primary/10 text-primary';
      case 'manager':
        return 'bg-blue-500/10 text-blue-600';
      case 'analyst':
        return 'bg-purple-500/10 text-purple-600';
      case 'viewer':
        return 'bg-gray-500/10 text-gray-600';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      user?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const activeCount = users?.filter((u) => u?.status === 'active')?.length;
  const inactiveCount = users?.filter((u) => u?.status === 'inactive')?.length;

  if (showAddUser) {
    return <AddUser onClose={() => setShowAddUser(false)} onUserAdded={handleUserAdded} />;
  }

  if (selectedUser) {
    const permissions = userPermissions?.[selectedUser?.id];
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            iconName="ArrowLeft"
            onClick={() => setSelectedUser(null)}
          >
            Back to Users
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-semibold">
              {getInitials(selectedUser?.fullName)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-foreground mb-1">{selectedUser?.fullName}</h2>
              <p className="text-muted-foreground mb-2">{selectedUser?.email}</p>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(selectedUser?.role)}`}>
                  {selectedUser?.role?.toUpperCase()}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(selectedUser?.status)}`}>
                  <Icon name={getStatusIcon(selectedUser?.status)} size={12} />
                  {selectedUser?.status?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Account Created</p>
              <p className="text-foreground">{formatTimestamp(selectedUser?.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Last Updated</p>
              <p className="text-foreground">{formatTimestamp(selectedUser?.updatedAt)}</p>
            </div>
          </div>

          {permissions && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Monitor" size={16} />
                  Screen Access
                </h3>
                <div className="space-y-2">
                  {permissions?.screens?.length > 0 ? (
                    permissions?.screens?.map((perm) => (
                      <div key={perm?.screenId} className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                        <Icon name="CheckCircle2" size={14} className="text-success" />
                        <span className="text-sm text-foreground">{perm?.screens?.displayName}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No screen access granted</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Zap" size={16} />
                  Feature Access
                </h3>
                <div className="space-y-2">
                  {permissions?.features?.length > 0 ? (
                    permissions?.features?.map((perm) => (
                      <div key={perm?.featureId} className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                        <Icon name="CheckCircle2" size={14} className="text-success" />
                        <span className="text-sm text-foreground">{perm?.features?.displayName}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No feature access granted</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Total Users
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {users?.length}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Users" size={20} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Active Users
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {activeCount}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="UserCheck" size={20} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Inactive Users
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {inactiveCount}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted flex items-center justify-center">
              <Icon name="UserX" size={20} className="text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 md:gap-4">
        <div className="relative flex-1">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search by name, email, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="analyst">Analyst</option>
            <option value="viewer">Viewer</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          {userProfile?.role === 'admin' && (
            <Button 
              variant="default" 
              iconName="UserPlus" 
              iconPosition="left"
              onClick={() => setShowAddUser(true)}
            >
              Add User
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {filteredUsers?.length > 0 ? (
            filteredUsers?.map((user) => (
              <div
                key={user?.id}
                className="bg-card border border-border rounded-lg p-4 md:p-6 hover:border-primary/30 transition-smooth"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-start gap-3 md:gap-4 flex-1">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm md:text-base font-semibold flex-shrink-0">
                      {getInitials(user?.fullName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
                          {user?.fullName}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${getStatusColor(
                            user?.status
                          )}`}
                        >
                          <Icon name={getStatusIcon(user?.status)} size={12} />
                          {user?.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 truncate">
                        {user?.email}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-medium ${getRoleBadgeColor(
                            user?.role
                          )}`}
                        >
                          {user?.role?.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Created: {formatTimestamp(user?.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {userProfile?.role === 'admin' && (
                    <div className="flex items-center gap-2 lg:flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        onClick={() => handleViewDetails(user)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
                        onClick={() => handleStatusToggle(user?.id, user?.status)}
                      >
                        {user?.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDeleteUser(user?.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsersOnSystem;